import {
  useState,
  useContext,
  useEffect,
  useMemo,
  RefObject,
  useRef,
  useCallback,
} from 'react'
import {
  convertPlayerCurrentTimeToTimestamp,
  isBetween,
  oneMinInFuture,
  id3CueDataToString,
  frameAccurateTimeCodeToTimeString,
  hasChaptersPlaceholder,
  isInsideLiveWindow,
} from './utils'
import { BrightcoveVideoWebPlayer } from '../types/darkcove'
import { Chapter, TimelinePointer } from '../types/story'

import BlickTVStoryContext from './BlickTVStoryContext'

type UseBlickTVStoryType = {
  playerRef: RefObject<BrightcoveVideoWebPlayer>
}

const emptyFn = () => {}

const findActiveChapter = (timestamp: string, chapters?: Chapter[]) => {
  return chapters?.find((chapter, index, chapters) =>
    isBetween(
      timestamp,
      chapter.timestamp,
      chapters[index + 1]?.timestamp || oneMinInFuture()
    )
  )
}
const removeObsoleteTimelinePointers = (
  timelinePointers: TimelinePointer[]
): TimelinePointer[] => {
  let wasFiltered = false

  const newTimelinePointers = timelinePointers.filter(
    (timelinePointer: TimelinePointer) => {
      const isPointerInsideLiveWindow = isInsideLiveWindow(
        timelinePointer.timeString,
        15
      )

      if (!wasFiltered && !isPointerInsideLiveWindow) {
        wasFiltered = true
      }

      return isPointerInsideLiveWindow
    }
  )
  // return previous array if it wasn't filtered (performance improvement)
  return wasFiltered ? newTimelinePointers : timelinePointers
}

const useBlickTVStory = ({
  playerRef,
}: UseBlickTVStoryType): {
  setCurrentTime: (currentTime: number) => void
  setAdPlaying: (adPlaying: boolean) => void
  onPlaySuccess: () => void
} => {
  const [currentTime, setCurrentTime] = useState<number>(0)
  const firstTimePlaySuccess = useRef<boolean>(true)

  const context = useContext(BlickTVStoryContext)
  const hasContext = !!context

  const {
    chapters = [],
    setActiveChapter = emptyFn,
    adPlaying,
    setAdPlaying = emptyFn,
    timelinePointers,
    setTimelinePointers = emptyFn,
    userLastSelectedTimestamp,
  } = context || {}

  /**
   * First step: Set the initial pointers on the EXT-X-PROGRAM-DATE-TIME
   * value of the manifest, that marks also the beginning of the program
   * Note: Due to Safari's unique implementation of the way it handles HLS,
   * there is a distinction between Safari and non-Safari browsers
   * Second step: Update the above pointers onCueChanges
   */
  const onPlaySuccess = useCallback(() => {
    if (!firstTimePlaySuccess.current || !hasContext) {
      return
    }
    firstTimePlaySuccess.current = false

    const player = playerRef?.current?.player?.()

    if (!player) {
      return
    }

    /**
     * Safari unique implementation
     */

    const safariStartDateTimeString = player
      ?.tech(true)
      // @ts-ignore
      ?.el?.()
      ?.getStartDate?.()

    if (safariStartDateTimeString) {
      /**
       *  start the pointer at the 8sec mark to account for streaming delays
       *  until we have the accurate time from the onCueChange
       */
      const pointer: TimelinePointer = {
        timeString: safariStartDateTimeString,
        timelineSecond: 8,
      }

      setTimelinePointers((oldArray) => [...oldArray, pointer])
    } else {
      /**
       * Non-Safari implementation for all other browsers
       */

      const playerHls = player?.tech(true).hls

      // the active media playlist
      const activeMediaPlaylist = playerHls?.playlists?.media()
      /**
       *  start the pointer at the 8sec mark to account for streaming delays
       *  until we have the accurate time from the onCueChange
       */
      if (activeMediaPlaylist?.dateTimeString) {
        const pointer: TimelinePointer = {
          timeString: activeMediaPlaylist?.dateTimeString,
          timelineSecond: 8,
        }

        setTimelinePointers((oldArray) => [...oldArray, pointer])
      }
    }

    /**
     * The function looks for the TCOD sent from the studio
     * along with the ID3 tags and updates the existing pointers
     */

    player.id3CuePointsTrack().on('cuechange', () => {
      setTimelinePointers((oldArray: TimelinePointer[]) => {
        const track = player.id3CuePointsTrack()
        const cues = track.cues && Array.from(track.cues)

        let newTimelinePointers = removeObsoleteTimelinePointers(oldArray)

        cues?.map((c: any) => {
          if (c.value.key === 'TCOD') {
            const timeCode = id3CueDataToString(c.value.data)
            if (timeCode) {
              const timeString = frameAccurateTimeCodeToTimeString(timeCode)

              if (timeString && c.startTime) {
                const pointer: TimelinePointer = {
                  timeString: timeString,
                  timelineSecond: c.startTime,
                }

                const existingPointer = oldArray.find(
                  (t: TimelinePointer) => t.timelineSecond === c.startTime
                )
                if (!existingPointer) {
                  newTimelinePointers = [...newTimelinePointers, pointer]
                }
              }
            }
          }
        })

        return newTimelinePointers
      })
    })
  }, [hasContext, playerRef, setTimelinePointers])

  const findAndSetActiveChapter = useCallback(
    (currentTime: number) => {
      if (timelinePointers.length > 0) {
        const timestamp = convertPlayerCurrentTimeToTimestamp(
          currentTime,
          timelinePointers
        )

        if (timestamp) {
          const activeChapter = findActiveChapter(timestamp, chapters)

          if (activeChapter) {
            setActiveChapter(activeChapter)
          }
        }
      }
    },
    [chapters, setActiveChapter, timelinePointers]
  )

  useEffect(() => {
    const player = playerRef.current?.player?.()
    const currentTime = player?.currentTime()

    if (hasContext && currentTime) {
      onPlaySuccess()
    }
  }, [hasContext, playerRef, onPlaySuccess])

  useEffect(() => {
    const player = playerRef?.current?.player?.()

    if (
      !player ||
      adPlaying ||
      !hasContext ||
      hasChaptersPlaceholder(chapters)
    ) {
      return
    }

    /**
     * since this operation is not time-sensitive, in order to avoid
     * jumping back and forth, due to not having close enough
     * timeline pointers (around ads mostly or when the user jumps close
     * to the end of the DVR window = 30min), we are calling the
     * setActiveChapter below only if the user did not
     * set it manually for more than 5sec
     */
    const shouldSetChapter =
      Date.now().valueOf() - userLastSelectedTimestamp > 5000

    if (shouldSetChapter) {
      findAndSetActiveChapter(currentTime)
    }
  }, [
    adPlaying,
    hasContext,
    currentTime,
    playerRef,
    findAndSetActiveChapter,
    userLastSelectedTimestamp,
    chapters,
  ])

  return useMemo(
    () => ({
      setCurrentTime,
      setAdPlaying,
      onPlaySuccess,
    }),
    [onPlaySuccess, setAdPlaying]
  )
}

export default useBlickTVStory
